/**
 * Wires the slick photo slider inside an open floorplan visual-content popup.
 * Keeps slide counter, captions, and video playback in sync as the user browses photos.
 * @param {Element} containerElement popup root (or ancestor containing the slider)
 * @param {{ photoId?: string|number }} [options] optional photo to open on init
 */
function initializeVcPopupPhotoScroller(containerElement, options) {
    options = options || {};
    const popup = containerElement.closest('.floorplan-vc-popup, .unit-vc-popup') || containerElement;
    const floorplanSliderContainer = containerElement.querySelector('.floorplanSliderContainer');
    const floorplanId = floorplanSliderContainer ? floorplanSliderContainer.dataset.floorplanId : '';

    const captionContainer = containerElement.getElementsByClassName('photo-caption')[0];
    const photoId = resolveFloorplanVcPopupPhotoId(popup, options.photoId);
    if (photoId != null) {
        rememberFloorplanVcPopupPhotoId(popup, photoId);
    }
    const initialSlide = findVcPopupPhotoSlideIndex(containerElement, floorplanId, photoId);

    const syncCaptionAndRemember = function (slick) {
        const currentImageSlide = slick.$slides[slick.currentSlide];
        const slideEl =
            currentImageSlide && currentImageSlide.nodeType === 1
                ? currentImageSlide
                : currentImageSlide && currentImageSlide[0];
        if (slideEl && captionContainer) {
            const image = slideEl.querySelector('img');
            captionContainer.innerHTML =
                image && image.getAttribute('data-caption') ? image.getAttribute('data-caption') : '';
        }
        rememberFloorplanVcPopupPhotoId(popup, getPhotoIdFromSlideElement(slideEl));
    };

    const initialCall = function (event, slick) {
        const sliderRoot = document.querySelector('.floorplanSlickSlider' + floorplanId);
        // One photo: stretch slick to full width and hide the slide counter
        if (sliderRoot && sliderRoot.querySelectorAll('.sliderItem').length === 1) {
            const containerWidth = sliderRoot.clientWidth;
            if (containerWidth != null) {
                const widthPx = containerWidth + 'px';
                const slickTrack = sliderRoot.querySelector('.slick-track');
                if (slickTrack != null) {
                    slickTrack.style.width = widthPx;
                }
                sliderRoot.querySelectorAll('.slick-track .slick-slide').forEach((slideEl) => {
                    slideEl.style.width = widthPx;
                });
            }
            const slideNumberText = document.getElementById('slideNumberText_' + floorplanId);
            if (slideNumberText) {
                slideNumberText.style.opacity = '0';
            }
        } else {
            const slideNumberText = document.getElementById('slideNumberText_' + floorplanId);
            if (slideNumberText) {
                slideNumberText.textContent = slick.currentSlide + 1 + ' of ' + slick.slideCount;
            }
        }
        syncCaptionAndRemember(slick);
    };

    const beforeSlidingCall = function (event, slick) {
        pauseAllVideoPlayers(floorplanId);
        const floorplanSlickRoot = document.querySelector('.floorplanSlickSlider' + floorplanId);
        if (floorplanSlickRoot && floorplanSlickRoot.classList.contains('slick-initialized')) {
            $(floorplanSlickRoot).slick('slickSetOption', 'swipe', true);
        }
    };

    const afterSlidingCall = function (event, slick) {
        const slideNumberTextAfter = document.getElementById('slideNumberText_' + floorplanId);
        if (slideNumberTextAfter) {
            slideNumberTextAfter.textContent = slick.currentSlide + 1 + ' of ' + slick.slideCount;
        }

        syncCaptionAndRemember(slick);

        // Second slide viewed counts as engaged VC interaction
        if (slick.currentSlide === 1) {
            trackEngagedAction(1);
        }
    };

    const loadOptions = {
        sliderIdClass: 'floorplanSlickSlider' + floorplanId,
        popupLinkClass: 'floorplan-photo-' + floorplanId,
        initialCallback: initialCall,
        beforeChangeCallback: beforeSlidingCall,
        afterChangeCallback: afterSlidingCall,
        prevArrowSelector: '.floorplanSliderContainer' + floorplanId + ' .floorplanSlider-arrow.prev',
        nextArrowSelector: '.floorplanSliderContainer' + floorplanId + ' .floorplanSlider-arrow.next',
        infinite: false,
        circularSlider: true,
    };
    if (initialSlide != null) {
        loadOptions.initialSlide = initialSlide;
    }

    // Popup DOM needs a moment before slick can measure layout
    setTimeout(function () {
        loadSlickSlider(loadOptions);
    }, 100);
    monitorAreaForKeyboardNavigation('floorplanSliderContainer_' + floorplanId);
}

/** @returns {string|null} photoId from a slide element (pre-slick or slick-wrapped). */
function getPhotoIdFromSlideElement(slideEl) {
    if (slideEl == null) {
        return null;
    }
    if (slideEl.dataset && slideEl.dataset.photoId) {
        return slideEl.dataset.photoId;
    }
    const nested = slideEl.querySelector ? slideEl.querySelector('[data-photo-id]') : null;
    return nested ? nested.dataset.photoId : null;
}

/** Persists the last viewed photo on the popup so section switches / return-stack can restore it. */
function rememberFloorplanVcPopupPhotoId(popup, photoId) {
    if (popup == null || photoId == null || photoId === '') {
        return;
    }
    popup.dataset.lastPhotoId = String(photoId);
}

/** @returns {string|null} */
function getRememberedFloorplanVcPopupPhotoId(popup) {
    if (popup == null) {
        return null;
    }
    const photoId = popup.dataset.lastPhotoId;
    return photoId != null && photoId !== '' ? photoId : null;
}

/**
 * Prefers an explicit photoId (deep-link), otherwise the last photo viewed in this popup.
 * @returns {string|number|null}
 */
function resolveFloorplanVcPopupPhotoId(popup, preferredPhotoId) {
    if (preferredPhotoId != null && preferredPhotoId !== '') {
        return preferredPhotoId;
    }
    return getRememberedFloorplanVcPopupPhotoId(popup);
}

/**
 * Finds the zero-based slide index for a photo id (works pre-slick and after slick wrap).
 * @returns {number|null}
 */
function findVcPopupPhotoSlideIndex(containerElement, floorplanId, photoId) {
    if (photoId == null || photoId === '') {
        return null;
    }
    const sliderRoot = containerElement.querySelector('.floorplanSlickSlider' + floorplanId);
    if (sliderRoot == null) {
        return null;
    }
    let slides = Array.from(sliderRoot.children).filter((el) => el.classList.contains('sliderItem'));
    if (slides.length === 0) {
        // Slick already initialized: read non-cloned slides in index order.
        slides = Array.from(sliderRoot.querySelectorAll('.slick-slide:not(.slick-cloned)')).sort(
            (a, b) => Number(a.dataset.slickIndex) - Number(b.dataset.slickIndex),
        );
    }
    const index = slides.findIndex((slide) => String(getPhotoIdFromSlideElement(slide)) === String(photoId));
    return index >= 0 ? index : null;
}

/**
 * Wires horizontal scroll arrows and keyboard navigation for video chapter buttons
 */
function initializeVcPopupVideoChapterScroller(containerElement) {
    // check to see if the video chapters div is rendered
    const videoActionsSection = containerElement.querySelector('.floorplan-vc-popup-actions-video');
    if (videoActionsSection == null) {
        return;
    }

    const videoActionsStyles = getComputedStyle(videoActionsSection);
    if (videoActionsStyles.display === 'none') {
        return;
    }

    const chaptersContainer = videoActionsSection.querySelector('.videoChaptersContainer');
    if (chaptersContainer == null) {
        return;
    }

    // Popup DOM needs a moment before scroll arrows can measure overflow
    setTimeout(function () {
        initializeHorizontalScrollContainerWithKeyboard(chaptersContainer, {
            itemSelector: '.videoChapterButton',
            getActiveItem: function () {
                return chaptersContainer.querySelector('.videoChapterButton'); // we don't persist which video chapter button is clicked, so always return the first button
            },
        });
    }, 100);
}

/**
 * Magnific open handler for floorplan visual-content popups.
 * Desktop inits slick in the popup; mobile lazy-loads a separate gallery popup.
 * @param {string} popupId popup element id or #id
 * @param {{ section?: string, photoId?: string|number, autoplay?: boolean }} [options] optional section / photo deep-link
 */
function floorplanVcPopupMagnificOpen(popupId, options) {
    options = options || {};
    const isDesktop = document.getElementsByTagName('body')[0].classList.contains('desktop-page');
    const cleanPopupId = popupId.startsWith('#') ? popupId.substring(1) : popupId;
    const popup = document.getElementById(cleanPopupId);
    if (popup == null) {
        return;
    }
    if (options.section) {
        setFloorplanVcPopupSection(popup, options.section, {
            photoId: options.photoId,
            initializePhotos: false,
        });
    }
    if (isDesktop) {
        const photosContainer = popup.getElementsByClassName('floorplan-vc-popup-photos')[0];
        if (photosContainer) {
            const photosContainerStyles = getComputedStyle(photosContainer);
            if (photosContainerStyles && photosContainerStyles.display !== 'none') {
                initializeVcPopupPhotoScroller(popup, {
                    photoId: resolveFloorplanVcPopupPhotoId(popup, options.photoId),
                });
            }
        }
    } else {
        populateImgGallerySRCs('floorplan-' + popup.dataset.floorplanId + '-gallery-popup');
    }
    initializeVcPopupVideoChapterScroller(popup);
    // Deep-link / ?vc=1 autoplay; skip when restoring after email/appointment (autoplay: false).
    if (options.section === 'video' && options.autoplay !== false) {
        requestFloorplanVcPopupVideoAutoplay(popup);
    }
}

/** Magnific close handler — stops any videos still playing in the popup. */
function floorplanVcPopupMagnificClose(popupId) {
    window.pendingFloorplanVcPopupAutoplayId = null;
    pauseAllVideoPlayers();
}

/**
 * Opens a floorplan VC / media popup via Magnific (shared by media-scroller + return-stack restore).
 * @param {string} popupId popup element id or #id
 * @param {{ section?: string, photoId?: string|number, autoplay?: boolean }} [options]
 */
function openFloorplanVcPopupMagnific(popupId, options) {
    if (typeof $ === 'undefined' || !$.magnificPopup) {
        return;
    }
    const cleanPopupId = popupId.startsWith('#') ? popupId.substring(1) : popupId;
    if (document.getElementById(cleanPopupId) == null) {
        return;
    }
    $.magnificPopup.open({
        items: { src: '#' + cleanPopupId },
        type: 'inline',
        midClick: true,
        closeBtnInside: true,
        callbacks: {
            open: function () {
                floorplanVcPopupMagnificOpen('#' + cleanPopupId, options);
            },
            close: function () {
                floorplanVcPopupMagnificClose('#' + cleanPopupId);
            },
        },
    });
}

/** @returns {string|null} Active VC section suffix, or null if none is visible. */
function getFloorplanVcPopupActiveSection(popup) {
    const suffixes = ['video', 'matterport', 'photos', 'floorplan-diagram'];
    for (let i = 0; i < suffixes.length; i++) {
        const section = popup.querySelector('.floorplan-vc-popup-' + suffixes[i]);
        if (section != null && getComputedStyle(section).display !== 'none') {
            return suffixes[i];
        }
    }
    return null;
}

/** @returns {string|null} photoId on the current slick slide, when photos are active. */
function getFloorplanVcPopupActivePhotoId(popup) {
    const floorplanId = popup && popup.dataset.floorplanId;
    if (!floorplanId) {
        return null;
    }
    const currentSlide = popup.querySelector(
        '.floorplanSlickSlider' + floorplanId + ' .slick-slide.slick-current:not(.slick-cloned)',
    );
    return getPhotoIdFromSlideElement(currentSlide);
}

/**
 * Builds restore options so reopening after email/appointment returns to the same tab (and photo).
 * @param {Element} popup
 * @returns {{ section?: string, photoId?: string, autoplay?: boolean }}
 */
function buildFloorplanVcPopupReturnOptions(popup) {
    const options = {};
    const section = getFloorplanVcPopupActiveSection(popup);
    if (!section) {
        return options;
    }
    options.section = section;
    if (section === 'video') {
        options.autoplay = false;
    }
    // Always carry the last photo so Photos can restore after return even if another tab is active.
    const photoId = getFloorplanVcPopupActivePhotoId(popup) || getRememberedFloorplanVcPopupPhotoId(popup);
    if (photoId != null) {
        options.photoId = photoId;
    }
    return options;
}

/**
 * When email/appointment/specials is opened from a floorplan VC / media popup CTA, push return state so
 * dismissing the child (or success) popup reopens this media popup.
 * No-op when the CTA is outside a VC popup (e.g. floorplan page header).
 * @param {Element} anchor CTA that was clicked
 */
function pushFloorplanVcPopupReturnStateFromCta(anchor) {
    if (typeof pushMagnificPopupReturnState !== 'function' || !$.magnificPopup.instance?.isOpen) {
        return;
    }
    const popup = anchor && anchor.closest ? anchor.closest('.floorplan-vc-popup') : null;
    if (popup == null || !popup.id) {
        return;
    }
    rememberFloorplanVcPopupPhotoId(popup, getFloorplanVcPopupActivePhotoId(popup));
    const restoreOptions = buildFloorplanVcPopupReturnOptions(popup);
    const popupId = popup.id;
    pushMagnificPopupReturnState(function () {
        openFloorplanVcPopupMagnific(popupId, restoreOptions);
    });
}

/** Magnific open from Media/Photos button (list card + floorplan media scroller). */
function floorplanVcPopupMagnificOpenFromButton(popupId) {
    trackGA4Event('open_vc_popup_vc_btn');
    floorplanVcPopupMagnificOpen(popupId);
}

/** Magnific open from clicking the floorplan list card image. */
function floorplanVcPopupMagnificOpenFromImage(popupId) {
    trackGA4Event('open_vc_popup_image');
    floorplanVcPopupMagnificOpen(popupId);
}

/**
 * Request autoplay for the floorplan video in a VC/media popup.
 * Plays immediately when the player is ready; otherwise waits for player onReady
 * (via playFloorplanVcPopupVideoIfPending from loadVideosScript).
 * @param {Element} popup popup root with data-floorplan-id
 */
function requestFloorplanVcPopupVideoAutoplay(popup) {
    const floorplanId = popup && popup.dataset.floorplanId;
    if (!floorplanId) {
        return;
    }
    window.pendingFloorplanVcPopupAutoplayId = String(floorplanId);
    playFloorplanVcPopupVideoIfPending();
}

/**
 * If a VC popup video autoplay is pending and the player is ready, start playback.
 * Called from popup open and from Wistia/YouTube onReady handlers.
 * @returns {boolean} true when play was started (or pending cleared)
 */
function playFloorplanVcPopupVideoIfPending() {
    const floorplanId = window.pendingFloorplanVcPopupAutoplayId;
    if (floorplanId == null || typeof videoPlayers === 'undefined') {
        return false;
    }
    const entry = videoPlayers.videos[floorplanId] || videoPlayers.videos[String(floorplanId)];
    if (!entry || !entry.videoPlayer) {
        return false;
    }

    try {
        if (entry.type === 'wistia' && typeof entry.videoPlayer.play === 'function') {
            entry.videoPlayer.play();
        } else if (entry.type === 'youtube' && typeof entry.videoPlayer.playVideo === 'function') {
            // playVideo before YT onReady can fail; only play when state is readable.
            if (typeof entry.videoPlayer.getPlayerState !== 'function') {
                return false;
            }
            entry.videoPlayer.getPlayerState();
            entry.videoPlayer.playVideo();
        } else {
            return false;
        }
    } catch (err) {
        return false;
    }

    window.pendingFloorplanVcPopupAutoplayId = null;
    return true;
}

/**
 * Switches the active tab in a floorplan visual-content popup (photos, video, 3D tour, etc.).
 * Shows the matching content panel and footer actions for the selected tab.
 * @param {Element} popup popup root element
 * @param {string} sectionClassSuffix e.g. 'video' | 'photos' | 'matterport' | 'floorplan-diagram'
 * @param {{ photoId?: string|number, initializePhotos?: boolean }} [options]
 */
function setFloorplanVcPopupSection(popup, sectionClassSuffix, options) {
    options = options || {};
    const initializePhotos = options.initializePhotos !== false;
    const isDesktop = document.getElementsByTagName('body')[0].classList.contains('desktop-page');
    const buttonContainer = popup.querySelector('.floorplan-vc-popup-toggle-buttons');
    const contentContainer = popup.getElementsByClassName('floorplan-vc-popup-content')[0];
    const actionsContainer = popup.getElementsByClassName('floorplan-vc-popup-content-actions')[0];
    if (contentContainer == null) {
        return;
    }

    // Explicit photo (deep-link / return-stack) wins; otherwise capture before leaving Photos.
    if (options.photoId != null && options.photoId !== '') {
        rememberFloorplanVcPopupPhotoId(popup, options.photoId);
    } else if (getFloorplanVcPopupActiveSection(popup) === 'photos') {
        rememberFloorplanVcPopupPhotoId(popup, getFloorplanVcPopupActivePhotoId(popup));
    }

    pauseAllVideoPlayers();

    if (buttonContainer) {
        Array.from(buttonContainer.children).forEach((button) => {
            const onclickAttr = button.getAttribute('onclick') || '';
            const isActive = onclickAttr.indexOf("'" + sectionClassSuffix + "'") !== -1;
            button.classList.toggle('active', isActive);
        });
    }

    Array.from(contentContainer.children).forEach((contentSection) => {
        if (contentSection.classList.contains('floorplan-vc-popup-' + sectionClassSuffix)) {
            contentSection.style.display = 'block';
        } else {
            contentSection.style.display = 'none';
        }
    });

    if (actionsContainer) {
        Array.from(actionsContainer.children).forEach((actions) => {
            if (actions.classList.contains('floorplan-vc-popup-actions-' + sectionClassSuffix)) {
                actions.style.display = 'block';
            } else {
                actions.style.display = 'none';
            }
        });
    }

    // Slick must re-init when returning to photos; the tab was hidden while inactive
    if (initializePhotos && isDesktop && sectionClassSuffix === 'photos') {
        initializeVcPopupPhotoScroller(popup, {
            photoId: resolveFloorplanVcPopupPhotoId(popup, options.photoId),
        });
    }

    if (sectionClassSuffix === 'video') {
        initializeVcPopupVideoChapterScroller(popup);
    }
}

/**
 * Programmatically selects a VC popup section (and optional photo) by popup id.
 * Does not open Magnific; call after the popup is shown, or pass section/photoId into
 * floorplanVcPopupMagnificOpen from a Magnific open callback.
 * @param {{ popupId: string, section?: string, photoId?: string|number }} options
 */
function openFloorplanVcPopup(options) {
    if (options == null || options.popupId == null) {
        return;
    }
    const cleanPopupId = options.popupId.startsWith('#') ? options.popupId.substring(1) : options.popupId;
    const popup = document.getElementById(cleanPopupId);
    if (popup == null) {
        return;
    }

    let section = options.section;
    if (!section && options.photoId != null && options.photoId !== '') {
        section = 'photos';
    }
    if (!section) {
        return;
    }

    setFloorplanVcPopupSection(popup, section, { photoId: options.photoId });
}

/**
 * Switches the active tab in a floorplan visual-content popup (photos, video, 3D tour, etc.).
 * Shows the matching content panel and footer actions for the selected tab.
 */
function toggleFloorplanVcPopupSection(event, sectionClassSuffix) {
    const popup = event.currentTarget.closest('.floorplan-vc-popup, .unit-vc-popup');
    if (popup == null) {
        return;
    }
    setFloorplanVcPopupSection(popup, sectionClassSuffix);
}

/**
 * Attaches Magnific to a link and invokes open/close callbacks named on the element.
 * Callback names live in data-popup-open-callback and data-popup-close-callback.
 */
function initOpenPopupLinkWithCallbacks(linkEl) {
    if (linkEl == null || linkEl.dataset.popupCallbacksWired === 'true') {
        return;
    }
    linkEl.dataset.popupCallbacksWired = 'true';
    $(linkEl).magnificPopup({
        type: 'inline',
        midClick: true,
        closeBtnInside: true,
        callbacks: {
            open: function () {
                if (this.ev.context.dataset.popupOpenCallback) {
                    const popupItem = $.magnificPopup.instance.currItem.src;
                    window[this.ev.context.dataset.popupOpenCallback](popupItem);
                }
            },
            close: function () {
                if (this.ev.context.dataset.popupCloseCallback) {
                    const popupItem = $.magnificPopup.instance.currItem.src;
                    window[this.ev.context.dataset.popupCloseCallback](popupItem);
                }
            },
        },
    });
}
